// @ts-check
/* eslint-disable jsdoc/require-description, jsdoc/require-rejects, jsdoc/require-tags */

/** @file Renders a reusable task timelog row with route or inline edit behavior. */

import {FlashNotifications} from "flash-notifications"
import _ from "gettext-universal/build/src/translate.js"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Text from "@kaspernj/api-maker/build/utils/text"

import Button from "../button"
import buildRoutePath from "../../shared/build-route-path"
import Card from "../card"
import confirmModal from "../../shared/confirm-modal"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {{can?: (ability: string) => boolean, comment: () => string | null, date: () => Date | string | null, destroy?: () => Promise<unknown>, id: () => string, invoiced: () => boolean, timeSeconds: () => number | null, user: () => {name: () => string | null} | null}} TimelogReadRecord */
/** @typedef {Record<string, string | string[] | undefined>} RouteParams */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogRowProps
 * @property {boolean} [canDelete] Whether the row exposes its delete command.
 * @property {boolean} [canEdit] Whether the row exposes its edit command.
 * @property {TimelogReadRecord} timelog Timelog record to render.
 * @property {RouteParams} [params] Current route params when editing through a route.
 * @property {((timelog: TimelogReadRecord) => void) | null} [onEditPress] Optional inline edit callback.
 * @property {((timelog: TimelogReadRecord) => Promise<void>) | null} [onDeletePress] Optional durable-id delete callback.
 * @property {() => Promise<void>} onDeleted Callback fired after deletion.
 * @property {string} [testID] Base test id for the row.
 */

/** @typedef {{deleting: boolean}} TimelogRowState */

/** Renders a timelog summary and its permitted actions. */
class TimelogRow extends ShapeComponent {
  static defaultProps = {
    canDelete: undefined,
    canEdit: undefined,
    onDeletePress: null,
    onEditPress: null,
    params: {},
    testID: ""
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {TimelogRowState} */
  state = {deleting: false}

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /** @returns {ReactNode} Timelog row content. */
  render() {
    const {timelog} = this.p
    const {deleting} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const timelogID = timelog.id()
    const testID = this.p.testID || `tasksTimelogsScreen/row-${timelogID}`
    const canEdit = this.p.canEdit ?? timelog.can?.("update") ?? false
    const canDelete = this.p.canDelete ?? timelog.can?.("destroy") ?? false
    const comment = timelog.comment()
    const dateLabel = formatDate(timelog.date())
    const userLabel = timelog.user()?.name() || null
    const metaParts = [dateLabel, userLabel].filter(isPresentString)
    return (
      <Card style={styles.row ||= {marginBottom: 12}} testID={testID}>
        <View style={styles.rowHeader ||= {alignItems: "flex-start", flexDirection: "row", gap: 12, justifyContent: "space-between"}} testID={`${testID}/header`}>
          <View style={styles.rowSummary ||= {flex: 1}} testID={`${testID}/summary`}>
            <Text style={styles.rowTime ||= {fontSize: 16, fontWeight: 600}} testID={`${testID}/time`}>
              {secondsToTime(timelog.timeSeconds())}
            </Text>
            {metaParts.length > 0 &&
              <Text style={styles[`rowMeta-${themeName}`] ||= {color: colors.textMuted, fontSize: 12, marginTop: 2}} testID={`${testID}/meta`}>
                {metaParts.join(" · ")}
              </Text>
            }
            {isPresentString(comment) &&
              <Text style={styles[`rowComment-${themeName}`] ||= {color: colors.textMuted, fontSize: 14, marginTop: 4}} testID={`${testID}/comment`}>
                {comment}
              </Text>
            }
            {timelog.invoiced() &&
              <Text style={styles[`rowInvoicedBadge-${themeName}`] ||= {color: colors.successText, fontSize: 12, fontWeight: 700, marginTop: 6, textTransform: "uppercase"}} testID={`${testID}/invoicedBadge`}>
                {_("Invoiced")}
              </Text>
            }
          </View>
          {(canEdit || canDelete) &&
            <View style={styles.rowActions ||= {flexDirection: "row", gap: 8}} testID={`${testID}/actions`}>
              {canEdit &&
                <Button
                  href={this.p.onEditPress ? undefined : buildRoutePath(this.p.params || {}, `/timelogs/${timelogID}/edit`)}
                  iconName="pen-to-square"
                  label={_("Edit")}
                  onPress={this.p.onEditPress ? () => this.p.onEditPress?.(timelog) : undefined}
                  testID={`${testID}/editButton`}
                  variant="outline"
                />
              }
              {canDelete &&
                <Button
                  disabled={deleting}
                  iconName="trash"
                  label={deleting ? _("Deleting...") : _("Delete")}
                  onPress={this.tt.onDeletePress}
                  testID={`${testID}/deleteButton`}
                  variant="destructive-outline"
                />
              }
            </View>
          }
        </View>
      </Card>
    )
  }

  /** @returns {Promise<void>} Resolves after the delete flow finishes. */
  onDeletePress = async () => {
    const ok = await confirmModal(_("Delete this timelog?"), {
      cancelLabel: _("Cancel"),
      confirmLabel: _("Delete"),
      title: _("Confirm delete"),
      tone: "destructive"
    })
    if (!ok) return
    this.s.deleting = true
    try {
      if (this.p.onDeletePress) {
        await this.p.onDeletePress(this.p.timelog)
      } else if (this.p.timelog.destroy) {
        await this.p.timelog.destroy()
      } else {
        throw new Error("Timelog delete command is unavailable")
      }
      await this.p.onDeleted()
      FlashNotifications.success(_("Timelog deleted"))
    } catch (error) {
      FlashNotifications.errorResponse(error)
      this.s.deleting = false
      throw error
    }
  }
}

/**
 * Formats seconds as hours and minutes.
 * @param {number | null} seconds Duration in seconds.
 * @returns {string} Formatted duration.
 */
function secondsToTime(seconds) {
  if (!seconds) return "0:00"
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - hours * 3600) / 60)
  return `${hours}:${String(minutes).padStart(2, "0")}`
}

/**
 * Formats a date-like value for display.
 * @param {Date | string | null | undefined} value Date-like value.
 * @returns {string | null} ISO date label.
 */
function formatDate(value) {
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === "string" && value.length >= 10) return value.slice(0, 10)
  return null
}

/**
 * Checks whether a metadata label should be shown.
 * @param {string | null} value Candidate label.
 * @returns {value is string} Whether the label is present.
 */
function isPresentString(value) { return typeof value === "string" && value.length > 0 }

/** @type {NamedExoticComponent<TimelogRowProps>} */
const MemoizedTimelogRow = memo(shapeComponent(TimelogRow))

export default MemoizedTimelogRow
